import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { capsules } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Trash2, Zap, ArrowLeft, Lock, CheckCircle2, Clock, Send } from "lucide-react";
import PageHead from "@/components/PageHead";

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export default function CapsuleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cap, setCap] = useState(null);

  const load = async () => {
    try {
      setCap(await capsules.get(id));
    } catch (e) {
      toast.error("Not found");
      navigate("/dashboard");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [id]);

  if (!cap) return <div className="p-12 text-white/40">Loading...</div>;

  const verifyUrl = `${window.location.origin}/verify/${cap.access_token}`;

  const copy = () => {
    navigator.clipboard.writeText(verifyUrl);
    toast.success("Link copied");
  };

  const releaseNow = async () => {
    if (!confirm("Release this capsule immediately? Recipient will be notified now.")) return;
    try {
      await capsules.releaseNow(cap.id);
      toast.success("Released");
      load();
    } catch (e) {
      toast.error("Release failed");
    }
  };

  const remove = async () => {
    if (!confirm("Delete this capsule? This cannot be undone.")) return;
    try {
      await capsules.remove(cap.id);
      toast.success("Deleted");
      navigate("/dashboard");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const resend = async () => {
    try {
      await capsules.resendNotification(cap.id);
      toast.success("Notification re-sent");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    }
  };

  return (
    <>
      <PageHead title="Capsule detail | ChronoVaults" noindex />
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
      <Link to="/dashboard" className="label-mono text-white/50 hover:text-terracotta inline-flex items-center gap-2" data-testid="back-link">
        <ArrowLeft className="w-3 h-3" /> Back to vault
      </Link>

      <div className="mt-8 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            {cap.status === "released" ? (
              <span className="label-mono px-2 py-1 border border-terracotta/40 text-terracotta">
                <CheckCircle2 className="inline w-3 h-3 mr-1" /> Delivered
              </span>
            ) : (
              <span className="label-mono px-2 py-1 border border-white/15 text-white/70">
                <Lock className="inline w-3 h-3 mr-1" /> Sealed
              </span>
            )}
          </div>
          <h1 className="font-serif text-5xl text-bone mt-4 leading-tight">{cap.title}</h1>
          {cap.message ? <p className="text-white/60 mt-4 max-w-2xl leading-relaxed">{cap.message}</p> : null}
        </div>

        <div className="flex gap-3">
          {cap.status !== "released" ? (
            <Button onClick={releaseNow} data-testid="release-now-btn" className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6">
              <Zap className="w-4 h-4 mr-2" /> Release now
            </Button>
          ) : (
            <Button onClick={resend} data-testid="resend-btn" variant="ghost" className="text-bone/80 hover:text-terracotta border border-white/10 rounded-full px-5">
              <Send className="w-4 h-4 mr-2" /> Re-send notification
            </Button>
          )}
          <Button onClick={remove} variant="ghost" data-testid="delete-btn" className="text-white/60 hover:text-red-400">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Details grid */}
      <div className="grid md:grid-cols-2 gap-px bg-white/5 mt-12">
        <div className="bg-vault-950 p-6">
          <p className="label-mono">Recipient</p>
          <p className="font-serif text-2xl text-bone mt-2">{cap.recipient_name}</p>
          <p className="text-xs text-white/50 mt-1">{cap.recipient_email}</p>
          <p className="text-xs text-white/50">{cap.recipient_phone}</p>
        </div>
        <div className="bg-vault-950 p-6">
          <p className="label-mono">Sender identity</p>
          <p className="font-serif text-2xl text-bone mt-2">{cap.sender_phone}</p>
          <p className="text-xs text-white/50 mt-1">Recipient will match this exact number.</p>
        </div>
        <div className="bg-vault-950 p-6">
          <p className="label-mono">Release event</p>
          <p className="font-serif text-2xl text-bone mt-2 flex items-center gap-2">
            <Clock className="w-4 h-4 text-terracotta" /> {fmt(cap.release_at)}
          </p>
          <p className="text-xs text-white/50 mt-1">Trigger: {cap.trigger_type}</p>
        </div>
        <div className="bg-vault-950 p-6">
          <p className="label-mono">Video</p>
          <p className="font-serif text-2xl text-bone mt-2">{cap.video_filename || "video"}</p>
          <p className="text-xs text-white/50 mt-1">{(cap.video_size / 1048576).toFixed(1)} MB · {cap.video_content_type}</p>
        </div>
      </div>

      {/* Public link */}
      <div className="mt-10 p-6 border border-white/10 bg-vault-800">
        <p className="label-mono">Recipient verification link</p>
        <p className="text-xs text-white/50 mt-2 mb-4">
          {cap.status === "released"
            ? "Recipient has been notified. They can use this link to verify and view."
            : "This link is dormant until release. It will be sent automatically."}
        </p>
        <div className="flex gap-3 items-center">
          <code className="flex-1 text-xs text-terracotta bg-vault-950 px-3 py-3 border border-white/5 overflow-x-auto" data-testid="verify-link">
            {verifyUrl}
          </code>
          <Button onClick={copy} variant="ghost" data-testid="copy-link" className="text-white/70 hover:text-bone">
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
    </>
  );
}
